import { Box } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import { NavLink, Outlet } from "react-router-dom";

const SUB_TABS = [
  { to: "workflows", label: "Workflows" },
  { to: "sla",       label: "SLA policies" },
] as const;

/**
 * Settings sub-nav item. Idle: muted, hover lifts to a soft surface.
 * Active (NavLink's auto `.active` class): brand-tinted soft pill +
 * brand text. styled(NavLink) keeps react-router's routing props and
 * the auto active class.
 */
const SubTab = styled(NavLink)(({ theme }) => ({
  borderRadius: 10,
  padding: "8px 12px",
  fontSize: 14,
  textDecoration: "none",
  color: theme.palette.text.disabled,
  transition: theme.transitions.create(["background-color", "color"], { duration: 150 }),
  "&:hover": {
    backgroundColor: theme.palette.surfaceSoft,
    color: theme.palette.text.primary,
  },
  "&.active": {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
    fontWeight: 500,
  },
  "&:focus-visible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 1,
  },
}));

/**
 * Settings shell — vertical sub-nav on desktop, top strip on mobile.
 * The Workflows panel (statuses + priorities) lives here now instead of
 * being its own top-level tab; SLA policies sit alongside it. KB
 * categories are managed in /documentation, so they're not a sub-tab
 * here. No outer Card chrome — each panel manages its own breathing
 * room so the page feels closer to a single document than a stack of
 * tabs.
 */
export function SettingsLayout(): JSX.Element {
  return (
    <Box sx={{ display: "grid", gap: { xs: 3, md: 5 }, gridTemplateColumns: { md: "200px 1fr" } }}>
      <Box
        component="nav"
        aria-label="Settings sections"
        sx={{
          mx: { xs: -0.5, md: 0 },
          display: "flex",
          flexDirection: { xs: "row", md: "column" },
          gap: 0.5,
          overflowX: { xs: "auto", md: "visible" },
        }}
      >
        {SUB_TABS.map((t) => (
          <SubTab key={t.to} to={t.to}>
            {t.label}
          </SubTab>
        ))}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Outlet />
      </Box>
    </Box>
  );
}
